import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
  EMPTY,
  Observable,
  catchError,
  defer,
  filter,
  forkJoin,
  map,
  of,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { WebsocketService } from '@core/services/websocket/websocket.service';
import { UserPreferencesActions } from '@core/store/user-preferences/user-preferences.actions';
import { resolveLanguage } from '@core/utils/language.utils';

import { AuthService } from '../../services/auth/auth.service';
import { GbeTokensService } from '../../services/gbe-tokens/gbe-tokens.service';
import * as AuthActions from './auth.actions';

/**
 * Safety net so a stuck preferences load (effect not registered, an HTTP layer
 * that never settles, …) can never hang rehydration — and thus the whole app —
 * indefinitely. Generous on purpose: a healthy load settles in ~100 ms.
 */
export const PREFERENCES_LOAD_TIMEOUT_MS = 10_000;

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly authService = inject(AuthService);
  private readonly gbeTokensService = inject(GbeTokensService);
  private readonly websocketService = inject(WebsocketService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly translate = inject(TranslateService);

  rehydrate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.rehydrateAuth),
      switchMap(() =>
        this.oidcSecurityService.checkAuth().pipe(
          switchMap(({ isAuthenticated, userData }) => {
            if (!isAuthenticated) {
              return of(AuthActions.rehydrationFailed());
            }

            this.applyKeycloakLocale(userData?.locale);

            // Wait for the OIDC token to be available in the synchronous cache
            // before calling the profile endpoint (avoids race condition where
            // checkAuth() resolves but the token subscription hasn't fired yet).
            return this.gbeTokensService.getAccessTokenRefreshing().pipe(
              filter((token) => !!token),
              take(1),
              // Load the user's GFE preferences alongside the profile so they
              // are in memory before any feature page (and its tables) renders.
              switchMap(() =>
                forkJoin({
                  user: this.authService.getUserProfile$(),
                  preferences: this.loadPreferences$()
                })
              ),
              map(({ user }) => AuthActions.rehydrationSuccess({ user })),
              catchError((error) => {
                // If the user is authenticated via OIDC but the profile fetch fails
                // with a server/network error, the backend or IDP is likely down.
                // Redirect to an error page instead of looping back to authorize().
                const status = error?.status ?? error?.error?.status;
                if (status === 0 || status === 502 || status === 503 || status === 504) {
                  this.router.navigate(['/api-down']);
                } else if (status === 401) {
                  // 401 while OIDC says authenticated = likely IDP unreachable
                  // (backend can't validate the KC token)
                  const msg = error?.error?.errorMessage ?? '';
                  if (
                    msg.includes('temporarily unavailable') ||
                    msg.includes('Identity provider')
                  ) {
                    this.router.navigate(['/api-down']);
                  }
                }
                return of(AuthActions.rehydrationFailed());
              })
            );
          }),
          catchError(() => of(AuthActions.rehydrationFailed()))
        )
      )
    )
  );

  // Initialize WebSocket after any successful authentication
  // This handles login, rehydration, and user switching in a unified way
  initializeWebSocketOnAuthSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.rehydrationSuccess),
        tap(({ user }) => {
          if (!isNil(user.id)) {
            // The initialize method is idempotent and handles user switching automatically
            this.websocketService.initialize(user.id);
          }
        })
      ),
    { dispatch: false }
  );

  /**
   * A failed refresh keeps the cached permissions rather than emitting a failure that would
   * clear them: losing the network is not evidence that a permission was revoked, and
   * failing open here is safe because the backend enforces the rule regardless.
   */
  refreshPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshPermissions),
      switchMap(() =>
        this.authService.getPermissions$().pipe(
          map((permissions) => AuthActions.refreshPermissionsSuccess({ permissions })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  refreshUserPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshUserPermissions),
      switchMap(() =>
        this.authService.getMyPermissions$().pipe(
          map((userPermissions) =>
            AuthActions.refreshUserPermissionsSuccess({ userPermissions })
          ),
          catchError(() => EMPTY)
        )
      )
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        switchMap(() => {
          this.websocketService.shutdown();
          this.gbeTokensService.clear();
          // Remove OIDC-related storage but keep the chosen UI language. User
          // preferences now live in the backend/store (reset via the
          // userPreferences reducer on logout), not in localStorage.
          const keysToKeep = ['gfe-lang'];
          Object.keys(localStorage).forEach((key) => {
            if (!keysToKeep.includes(key)) {
              localStorage.removeItem(key);
            }
          });
          return this.oidcSecurityService.logoff().pipe(catchError(() => EMPTY));
        })
      ),
    { dispatch: false }
  );

  /**
   * Triggers the store-driven preferences load and resolves once it has settled
   * (success or failure). Folded into the rehydration `forkJoin` so preferences
   * are in the store before any feature page renders. It never errors — a failed
   * load emits `loadFailure` (an action), so it cannot fail the rehydration join.
   */
  private loadPreferences$(): Observable<unknown> {
    return defer(() => {
      const settled$ = this.actions$.pipe(
        ofType(UserPreferencesActions.loadSuccess, UserPreferencesActions.loadFailure),
        take(1),
        // Never let a stuck load hang rehydration: fall through after a generous
        // delay so the app still becomes usable (preferences just stay empty).
        timeout({ first: PREFERENCES_LOAD_TIMEOUT_MS, with: () => of(undefined) })
      );
      // Dispatched synchronously; the load GET is async, so `settled$` is
      // subscribed before the success/failure action can be emitted.
      this.store.dispatch(UserPreferencesActions.load());
      return settled$;
    });
  }

  // Keep track of passed language
  private applyKeycloakLocale(locale: string | undefined): void {
    if (!locale) return;
    const lang = resolveLanguage(locale);
    if (!lang) return;
    localStorage.setItem('gfe-lang', lang);
    this.translate.use(lang).subscribe();
  }
}
